import { useState } from "react";
import {
  Title, Group, Button, TextInput, Select, Table, ActionIcon, Stack, Text, Card, Badge, Menu,
} from "@mantine/core";
import { IconPlus, IconSearch, IconDots, IconPencil, IconTrash, IconHistory, IconEye } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { type Asset, AssetType } from "@iso27001/shared";
import { assetsApi } from "../../api/assets.js";
import { AssetFormModal } from "./AssetFormModal.js";
import { AuditHistoryModal } from "../../components/AuditHistoryModal.js";
import { useAuth } from "../../auth/AuthContext.js";
import { useEnumLabel } from "../../i18n/enum-label.js";
import { useRecordDialog } from "../../components/use-record-dialog.js";
import { rowOpenProps, stopRowOpen, cellBadgeStyles, nowrap } from "../../components/list-table.js";
import { useUserName } from "../../components/use-user-name.js";

const TYPE_COLOR: Record<AssetType, string> = {
  Mitarbeiter: "blue",
  Prozess: "cyan",
  Device: "grape",
  Daten: "orange",
  Information: "teal",
  Sonstiges: "gray",
};

const CLASSIFICATION_COLOR: Record<string, string> = {
  öffentlich: "green",
  intern: "yellow",
  vertraulich: "red",
};

export function AssetsListPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "editor";
  const qc = useQueryClient();
  const { t } = useTranslation();
  const { label } = useEnumLabel();
  const userName = useUserName();

  const typeFilterOptions = [
    { value: "", label: t("assets.list.filter.allTypes") },
    ...AssetType.options.map((value) => ({ value, label: label("assetType", value) })),
  ];

  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [auditId, setAuditId] = useState<number | null>(null);
  const dialog = useRecordDialog<Asset>();

  const list = useQuery({
    queryKey: ["assets", { q, type: typeFilter }],
    queryFn: () => assetsApi.list({
      q: q || undefined,
      type: (typeFilter || undefined) as AssetType | undefined,
      pageSize: 200,
    }),
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => assetsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      notifications.show({ message: t("assets.list.notify.deleted"), color: "teal" });
    },
  });

  const onDelete = (a: Asset) => {
    modals.openConfirmModal({
      title: t("assets.list.confirmDelete.title"),
      children: <Text size="sm">{t("assets.list.confirmDelete.message", { name: a.name })}</Text>,
      labels: { confirm: t("common.action.delete"), cancel: t("common.action.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => removeMut.mutate(a.id),
    });
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>{t("assets.list.title")}</Title>
        {canEdit && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => dialog.open(null)}>
            {t("assets.newAsset")}
          </Button>
        )}
      </Group>
      <Card withBorder padding="xs">
        <Group>
          <TextInput
            placeholder={t("common.search.placeholder")}
            leftSection={<IconSearch size={14} />}
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            style={{ flex: 1, maxWidth: 320 }}
          />
          <Select
            data={typeFilterOptions}
            value={typeFilter}
            onChange={(val) => setTypeFilter(val ?? "")}
            style={{ width: 180 }}
            placeholder={t("assets.list.filter.allTypes")}
          />
          <Text size="sm" c="dimmed">{list.data ? t("common.entriesCount", { count: list.data.total }) : ""}</Text>
        </Group>
      </Card>
      <Card withBorder padding={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={nowrap}>{t("common.field.code")}</Table.Th>
              <Table.Th>{t("common.field.name")}</Table.Th>
              <Table.Th style={nowrap}>{t("assets.field.type")}</Table.Th>
              <Table.Th style={nowrap}>{t("assets.field.classification")}</Table.Th>
              <Table.Th style={nowrap}>{t("assets.form.assetOwner.label")}</Table.Th>
              <Table.Th>{t("common.field.description")}</Table.Th>
              <Table.Th style={{ width: 48 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {list.data?.items.map((a) => (
              <Table.Tr key={a.id} {...rowOpenProps(() => dialog.open(a))}>
                <Table.Td><Text ff="monospace" size="sm" style={nowrap}>{a.code}</Text></Table.Td>
                <Table.Td><Text size="sm">{a.name}</Text></Table.Td>
                <Table.Td>
                  <Badge color={TYPE_COLOR[a.type]} variant="light" styles={cellBadgeStyles}>{label("assetType", a.type)}</Badge>
                </Table.Td>
                <Table.Td>
                  {a.classification && (
                    <Badge color={CLASSIFICATION_COLOR[a.classification]} variant="outline" styles={cellBadgeStyles}>
                      {label("clearance", a.classification)}
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" style={nowrap}>{userName(a.assetOwnerUserId)}</Text>
                </Table.Td>
                <Table.Td>
                  {/* First line only: descriptions are line-structured, and run together they read as one jumble. */}
                  <Text size="sm" lineClamp={1} c="dimmed">{a.description?.split("\n")[0] ?? ""}</Text>
                </Table.Td>
                <Table.Td {...stopRowOpen}>
                  <Menu position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" aria-label={t("common.action.actions")}><IconDots size={16} /></ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {canEdit ? (
                        <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => dialog.open(a)}>
                          {t("common.action.edit")}
                        </Menu.Item>
                      ) : (
                        <Menu.Item leftSection={<IconEye size={14} />} onClick={() => dialog.open(a)}>
                          {t("common.action.open")}
                        </Menu.Item>
                      )}
                      <Menu.Item leftSection={<IconHistory size={14} />} onClick={() => setAuditId(a.id)}>
                        {t("common.action.history")}
                      </Menu.Item>
                      {canEdit && (
                        <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => onDelete(a)}>
                          {t("common.action.delete")}
                        </Menu.Item>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
            {list.data?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text ta="center" c="dimmed" py="md">{t("assets.list.empty")}</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>
      <AssetFormModal key={dialog.key} opened={dialog.opened} onClose={dialog.close} initial={dialog.record} />
      <AuditHistoryModal
        opened={auditId !== null}
        onClose={() => setAuditId(null)}
        queryKey={["assets", "audit", auditId]}
        fetchFn={() => assetsApi.audit(auditId!)}
      />
    </Stack>
  );
}
